import { useEffect, useRef, useState } from "react";
import { Button, ImageWithRetry } from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import AdminHooks from "@src/context/hooks/adminHooks";
import { AppUtils } from "@src/utils";
import { CUSTOMER_LOGO_URLS, NO_IMAGE_FOUND } from "@src/constants/constants";

const LOGO_TYPES = [
  { key: "square", label: "Square logo" },
  { key: "standard", label: "Standard logo" },
  { key: "footer", label: "Footer logo" },
];

const LogoCard = ({ customerCode, type, currentUrl }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const [dimensions, setDimensions] = useState(null);
  const inputRef = useRef(null);

  const uploadMutation = AdminHooks.useUploadCustomerLogo();

  // Revoke the object URL when the selected file changes / on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleSelect = (e) => {
    setError("");
    setSubmitted(false);
    const selected = e.target.files?.[0] || null;
    if (!selected) return;
    if (selected.type !== "image/png") {
      setError("Please choose a PNG image.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file) return;
    setError("");
    setSubmitted(false);
    try {
      await uploadMutation.mutateAsync({
        customerCode,
        logoType: type.key,
        file,
      });
      setSubmitted(true);
      reset();
      // Cache-bust the live preview so the replacement shows immediately.
      setCacheBust((n) => n + 1);
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    }
  };

  const liveSrc = cacheBust
    ? AppUtils.setUrlQueryParams(currentUrl, { _: cacheBust })
    : currentUrl;

  const fileName = currentUrl.split("/").pop();

  return (
    <CardContainer padding={4} flexibleHeight>
      <h4 className="pb-1 mb-0">{type.label}</h4>
      <a
        href={currentUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Open the original image in S3"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-700 pb-3"
      >
        {fileName} <span className="text-sm leading-none">↗</span>
      </a>

      <div className="flex flex-wrap items-start gap-8">
        <div>
          <div className="text-xs font-bold pb-1">
            Current version
            {dimensions && (
              <span className="font-normal text-neutral-500">
                {" "}
                ({dimensions.w}px × {dimensions.h}px)
              </span>
            )}
          </div>
          <ImageWithRetry
            key={`${type.key}-${cacheBust}`}
            src={liveSrc}
            alt={`${type.label} for ${customerCode}`}
            className="max-h-24 max-w-48 object-contain border border-neutral-200 bg-white p-1 rounded"
            onLoad={(e) => {
              // Ignore the not-found fallback so we report the real image's size.
              if (e.target.src.includes("no_image_found")) {
                setDimensions(null);
                return;
              }
              setDimensions({
                w: e.target.naturalWidth,
                h: e.target.naturalHeight,
              });
            }}
            onError={(e) => {
              e.target.src = NO_IMAGE_FOUND;
            }}
          />
        </div>

        {previewUrl && (
          <div>
            <div className="text-xs font-bold pb-1">New (preview)</div>
            <img
              src={previewUrl}
              alt={`New ${type.label}`}
              className="max-h-24 max-w-48 object-contain border border-primary-300 bg-white p-1 rounded"
            />
          </div>
        )}
      </div>

      <div className="pt-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/png"
          onChange={handleSelect}
          className="text-sm text-neutral-500 cursor-pointer
            file:cursor-pointer file:mr-3 file:py-1 file:px-4 file:rounded-2xl
            file:border file:border-primary-500 file:bg-primary-100 file:text-primary-800
            file:text-sm file:font-bold file:shadow hover:file:bg-primary-200
            file:transition-all"
        />
      </div>

      {error && <p className="text-red-500 text-sm pt-2">{error}</p>}
      {submitted && (
        <p className="text-green-600 text-sm pt-2">
          Logo uploaded successfully.
        </p>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button
          icon="save"
          block={false}
          disabled={!file || uploadMutation.isPending}
          loading={uploadMutation.isPending}
          onClick={handleUpload}
        >
          Upload
        </Button>
        {file && (
          <Button
            block={false}
            secondary
            color="neutral"
            disabled={uploadMutation.isPending}
            onClick={reset}
          >
            Cancel
          </Button>
        )}
      </div>
    </CardContainer>
  );
};

export const CustomerLogosSection = ({ customer }) => {
  const customerCode = customer?.customerCode;
  const urls = CUSTOMER_LOGO_URLS(customerCode);

  return (
    <div className="p-4 flex flex-col gap-4">
      <p className="text-sm text-neutral-500">
        Upload or replace the public PNG logos used by Signol emails for this
        customer. New images replace the existing ones in S3.
      </p>
      {LOGO_TYPES.map((type) => (
        <LogoCard
          key={type.key}
          customerCode={customerCode}
          type={type}
          currentUrl={urls[type.key]}
        />
      ))}
    </div>
  );
};

export default CustomerLogosSection;
